import React, { useEffect } from "react";
import Modal from "../Modals/Modal";
import ImageCropper from "../../Organismos/ImageCropper/ImageCropper";
import Image from "../../Atomos/Icons/Image";
import NewTooltip from "../Tooltip/NewTooltip";

type inputImageProps = {
  maxsize?: number;
  width?: number;
  height?: number;
  maxPreviewWidth?: number;
  maxPreviewHeight?: number;
  extension?: string;
  photo?: string;
  setImage: (photo: string | File | null) => void;
  title?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  tooltip?: string;
  error?: boolean;
  errorMsg?: string;
  adaptiveShape?: boolean;
  dynamicSize?: boolean;
  /** Crop area follows uploaded image aspect ratio (firm logos). */
  adaptCropToImage?: boolean;
  hideBorder?: boolean;
  modalClassName?: string;
};

const InputImage: React.FC<inputImageProps> = ({
  icon = <Image />,
  ...props
}: inputImageProps) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [draggedPhoto, setDraggedPhoto] = React.useState<
    string | File | null
  >();
  const [newphoto, setPhoto] = React.useState<File | null>(null);
  const [imageDimensions, setImageDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const aspectRatio = React.useMemo(() => {
    if (props.dynamicSize && imageDimensions) {
      return imageDimensions.width / imageDimensions.height;
    }
    return props.adaptiveShape && props.width && props.height 
      ? props.width / props.height 
      : 1;
  }, [props.adaptiveShape, props.width, props.height, props.dynamicSize, imageDimensions]);

  const containerStyle = React.useMemo(() => {
    if (!props.dynamicSize) {
      return props.adaptiveShape ? { aspectRatio: aspectRatio.toString() } : undefined;
    }

    if (!imageDimensions) {
      const defaultW = props.maxPreviewWidth ?? 500;
      const defaultH = props.maxPreviewHeight ?? 300;
      return {
        width: `${defaultW}px`,
        height: `${defaultH}px`,
      };
    }

    const { width: imgWidth, height: imgHeight } = imageDimensions;
    const maxW = props.maxPreviewWidth ?? 0;
    const maxH = props.maxPreviewHeight ?? 0;
    let width = imgWidth;
    let height = imgHeight;
    if (maxW > 0 && width > maxW) {
      width = maxW;
      height = Math.round((height * maxW) / imgWidth);
    }
    if (maxH > 0 && height > maxH) {
      height = maxH;
      width = Math.round((width * maxH) / height);
    }
    return {
      width: `${width}px`,
      height: `${height}px`,
      aspectRatio: aspectRatio.toString(),
    };
  }, [props.dynamicSize, imageDimensions, aspectRatio, props.adaptiveShape, props.maxPreviewWidth, props.maxPreviewHeight]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (props.dynamicSize && e.currentTarget) {
      setImageDimensions({
        width: e.currentTarget.naturalWidth,
        height: e.currentTarget.naturalHeight
      });
    }
  };

  useEffect(() => {
    if (props.dynamicSize && newphoto instanceof File) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          const img = document.createElement('img');
          img.onload = () => {
            setImageDimensions({
              width: img.naturalWidth,
              height: img.naturalHeight
            });
          };
          img.src = event.target.result as string;
        }
      };
      reader.readAsDataURL(newphoto);
    }
  }, [newphoto, props.dynamicSize]);

  const toggleModal = () => {
    if (!isModalOpen && props.photo) {
      // If opening modal with existing photo, set it as draggedPhoto for the cropper
      setDraggedPhoto(props.photo);
    }
    setIsModalOpen(!isModalOpen);
    document.body.style.overflow = !isModalOpen ? "hidden" : "";
  };

  useEffect(() => {
    if (newphoto) {
      props.setImage(newphoto);
      setImageDimensions(null);
      if (props.dynamicSize && newphoto instanceof File) {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            const img = document.createElement('img');
            img.onload = () => {
              setImageDimensions({
                width: img.naturalWidth,
                height: img.naturalHeight
              });
            };
            img.src = event.target.result as string;
          }
        };
        reader.readAsDataURL(newphoto);
      }
    }
  }, [newphoto, props.dynamicSize]);

  useEffect(() => {
    if (!props.photo) {
      setImageDimensions(null);
    }
  }, [props.photo]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === "string") {
          setDraggedPhoto(event.target.result);
          if (!props.dynamicSize) {
            toggleModal();
          }
        }
      };
      reader.readAsDataURL(droppedFile);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === "string") {
          setDraggedPhoto(event.target.result);
          toggleModal();
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <Modal
        title={props.title ? props.title : "Upload Profile Image"}
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
        className={props.modalClassName}
      >
        <ImageCropper
          setPhoto={setPhoto}
          onHandleUpload={() =>{}}
          draggedPhoto={draggedPhoto}
          onImageUpload={() => {
            toggleModal();
          }}
          shape={props.adaptiveShape || props.dynamicSize ? "rect" : "round"}
          aspectRatio={props.dynamicSize ? undefined : aspectRatio}
          adaptCropToImage={props.adaptCropToImage}
          showDimensions={props.dynamicSize && !props.adaptCropToImage}
        />
      </Modal>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />
      <div className={`flex flex-col gap-2 lg:gap-6 h-full ${props.hideBorder ?"" :"border p-2"} ${props.error ? "errorInput" : ""}`}>
          <div className={`flex not-italic font-bold
            ${props.disabled ? "text-[#C2C2C2]" : "text-gray-font"} justify-center`}
          >
            <p className="font-semibold">{props.title} {props.required && <span className="text-primary"> * </span>}{props.tooltip && <NewTooltip text={props.tooltip}/>}</p>
          </div>

          <div className={`flex flex-col items-center gap-2 [&>*:last-child]:gap-0`}>
            <div
              className={`w-full h-full flex flex-col gap-2 items-center justify-center`}
            >
              <div
                className={`bg-[#F4F4F4] ${props.adaptiveShape || props.dynamicSize ? 'rounded-lg' : 'rounded-full'} ${!props.adaptiveShape && !props.dynamicSize && 'h-[135px] lg:h-[204px]'}
                border-[1px] border-dashed border-[#BEBDBD] relative flex items-center justify-center ${props.dynamicSize ? '' : 'w-[135px] lg:w-[204px]'} overflow-hidden`}
                style={containerStyle}
                onClick={props.disabled ? () => "" : toggleModal}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(e);
                  toggleModal();
                }}
              >
                <div className="relative w-full h-full flex justify-center items-center">
                  {newphoto ? (
                    <img
                      src={URL.createObjectURL(newphoto)}
                      alt="Profile_Picture"
                      onLoad={handleImageLoad}
                      className={`${props.dynamicSize ? 'object-contain' : 'object-cover'} max-w-full max-h-full ${props.adaptiveShape || props.dynamicSize ? 'rounded-lg' : 'rounded-full'}`}
                      style={props.dynamicSize ? {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      } : undefined}
                    />
                  ) : props.photo ? (
                    <img
                      src={props.photo}
                      alt="Profile_Picture"
                      onLoad={handleImageLoad}
                      className={`${props.dynamicSize ? 'object-contain' : 'object-cover'} max-w-full max-h-full ${props.adaptiveShape || props.dynamicSize ? 'rounded-lg' : 'rounded-full'}`}
                      style={props.dynamicSize ? {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      } : undefined}
                    />
                  ) : (
                    <div className="w-[3.75rem] h-[3.75rem] lg:w-[6rem] lg:h-[6rem] p-4 fill-gray-font">
                      {icon}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
            <div className="flex flex-col items-center text-sm text-gray-600">
              <span className=" text-gray-600 not-italic">
                Click or Drag
              </span>
              {props.maxsize && (
                <span className="not-italic">
                  {props.maxsize} MB max.
                </span>
              )}
              {props.extension && (
                <span className=" not-italic text-center">
                  Accepted File Types are: {props.extension}.
                </span>
              )}
              {props.width && props.height && (
                <span className=" not-italic">
                  {props.width} x {props.height} pixels.
                </span>
              )}
            </div>

          {props.errorMsg && (
            <div className="flex flex-wrap justify-center items-center text-base font-medium text-primary w-full">
              {props.errorMsg}
            </div>
          )}
      </div>
    </>
  );
};
export default InputImage;
